import { useState } from "react";
import { Folder, Plus } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { EntityListRow } from "~/pages/manifold-nextgen/components/EntityListRow";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { nextGenDatasetDetailPath } from "~/router/constants";
import { projectDatasetsPath, setProjectDatasets } from "~/services/projects";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { useProjectContext } from "./useProjectContext";

export const ProjectDatasets = () => {
  const { projectId, attachedDatasets, isOwner } = useProjectContext();
  const { showSuccess } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { execute: removeDataset, isPending: isRemoving } = useAsyncMutation(
    (ids: string[]) => setProjectDatasets(projectId, ids),
    {
      errorMessage: "Failed to remove dataset",
      onSuccess: async () => {
        await mutate(projectDatasetsPath(projectId));
        showSuccess("Dataset removed");
      },
    }
  );

  const handleRemove = (datasetId: string) => {
    const remaining = (attachedDatasets ?? []).filter((d) => d.id !== datasetId).map((d) => d.id);
    void removeDataset(remaining);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <TabHeader title="Datasets" />
        <div className="flex items-center justify-end gap-3">
          {isOwner && (
            <Button
              variant="ghost"
              className="text-primary hover:text-primary/80"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="size-4" />
              Add Datasets
            </Button>
          )}
        </div>

        {attachedDatasets === undefined ? null : attachedDatasets.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">No datasets attached.</p>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {attachedDatasets.map((dataset) => (
              <EntityListRow
                key={dataset.id}
                href={nextGenDatasetDetailPath(dataset.id)}
                leading={
                  <Folder className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                }
                title={dataset.title}
                description={dataset.description}
                onRemove={isOwner ? () => handleRemove(dataset.id) : undefined}
                removeLabel={`Remove ${dataset.title}`}
                isRemoving={isRemoving}
              />
            ))}
          </ul>
        )}

        <ProjectDatasetsDialog
          projectId={projectId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          attachedIds={attachedDatasets?.map((d) => d.id) ?? []}
        />
      </div>
    </div>
  );
};
